import React from 'react';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import { NotificationStatus } from 'types/Notifications';
import {
  ZfBadge,
  ZfBox,
  ZfDivider,
  ZfDropdown,
  ZfDropdownItem,
  ZfIcon,
  ZfListItem,
  ZfStack,
  ZfTypography,
} from '@zafire/react';

type NotificationRowProps = {
  id: string;
  isNew: boolean;
  title: string;
  notificationDateTime: string;
  notificationText: string;
  onNotificationClick: (id: string) => void;
  onMoreIconClick: (id: string, newStatus: NotificationStatus) => void;
  style: React.CSSProperties;
};
/**
 * Component to display notification row
 */
export const NotificationRow = ({
  id,
  isNew,
  title,
  notificationDateTime,
  notificationText,
  onNotificationClick,
  onMoreIconClick,
  style,
}: NotificationRowProps) => {
  const MAX_NOTIFICATION_LENGTH = 150; // characters
  const MAX_NOTIF_TITLE_LEN = 20; // char

  const { t } = useTranslation();

  const onNotificationClickHandler = () => {
    onNotificationClick(id);
  };
  const onMoreIconClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    onMoreIconClick(id, isNew ? NotificationStatus.READ : NotificationStatus.UNREAD);
    e.stopPropagation();
  };

  const formattedNotificationDateTime = moment(notificationDateTime).format('DD/MM/YYYY HH:mm');
  const notificationTitle = title.length > MAX_NOTIF_TITLE_LEN ? `${title.slice(0, MAX_NOTIF_TITLE_LEN)}...` : title;
  const notificationDescription =
    notificationText.length > MAX_NOTIFICATION_LENGTH
      ? `${notificationText.slice(0, MAX_NOTIFICATION_LENGTH)}...`
      : notificationText;

  return (
    <ZfStack style={style}>
      <ZfListItem onClick={onNotificationClickHandler}>
        <ZfStack slot="leading" direction="row">
          <ZfStack style={{ rowGap: 'var(--padding-vertical-xxs)' }}>
            <ZfStack direction="row" style={{ columnGap: 'var(--gap-horizontal-xs)' }}>
              <ZfBox>
                <ZfStack direction="row" alignment="center">
                  {isNew ? (
                    <ZfBadge type="empty" size="small" style={{ marginRight: 'var(--gap-horizontal-md)' }} />
                  ) : null}
                  <ZfTypography variant="label-medium" weight="bold">
                    {notificationTitle}
                  </ZfTypography>
                </ZfStack>
              </ZfBox>
              <ZfBox>
                <ZfTypography variant="body-medium">{formattedNotificationDateTime}</ZfTypography>
              </ZfBox>
            </ZfStack>
            <ZfBox>
              <ZfTypography>{notificationDescription}</ZfTypography>
            </ZfBox>
          </ZfStack>
          <ZfStack>
            <ZfDropdown hierarchy="tertiary">
              <ZfIcon
                slot="icon"
                name="more_vert"
                size="large"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              <ZfDropdownItem
                data-testid="mark-read-unread"
                label={isNew ? t('notification.mark-read-label') : t('notification.mark-unread-label')}
                onClick={(event) => onMoreIconClickHandler(event)}
              />
            </ZfDropdown>
          </ZfStack>
        </ZfStack>
      </ZfListItem>
      <ZfDivider style={{ marginTop: 'var(--padding-vertical-sm)' }} />
    </ZfStack>
  );
};
